import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setActiveWorkspace } from './api.ts';

export interface SessionUser { id: string; email: string; name: string; created_at: string }
export interface SessionWorkspace {
  id: string; name: string; slug: string; is_demo: 0 | 1; created_at: string;
  role: 'owner' | 'admin' | 'editor' | 'analyst' | 'viewer';
}

interface SessionValue {
  user: SessionUser | null;
  workspaces: SessionWorkspace[];
  workspace: SessionWorkspace | null;
  loading: boolean;
  selectWorkspace: (id: string) => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  canWrite: boolean;
  isDemo: boolean;
}

const SessionContext = createContext<SessionValue>({
  user: null, workspaces: [], workspace: null, loading: true,
  selectWorkspace: () => {}, refresh: async () => {}, signOut: async () => {},
  canWrite: false, isDemo: false,
});

const STORAGE_KEY = 'orbs.workspace';

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [workspaces, setWorkspaces] = useState<SessionWorkspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await api<{ user: SessionUser; workspaces: SessionWorkspace[] }>('/api/auth/me', { workspace: null });
      setUser(data.user);
      setWorkspaces(data.workspaces);
      setWorkspaceId((current) => {
        const valid = current && data.workspaces.some((w) => w.id === current);
        // Prioriza um workspace real; o de demonstração é o último recurso.
        const fallback = data.workspaces.find((w) => !w.is_demo) ?? data.workspaces[0];
        return valid ? current : (fallback?.id ?? null);
      });
    } catch {
      setUser(null);
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // Precisa valer já na renderização: os efeitos dos filhos rodam antes dos do
  // pai, então um `useEffect` aqui deixaria a primeira requisição sem workspace.
  setActiveWorkspace(workspaceId);

  useEffect(() => {
    try {
      if (workspaceId) localStorage.setItem(STORAGE_KEY, workspaceId);
    } catch { /* storage bloqueado */ }
  }, [workspaceId]);

  const signOut = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
    setWorkspaces([]);
    window.location.href = '/login';
  }, []);

  const workspace = useMemo(
    () => workspaces.find((w) => w.id === workspaceId) ?? null,
    [workspaces, workspaceId],
  );

  const value: SessionValue = {
    user,
    workspaces,
    workspace,
    loading,
    selectWorkspace: setWorkspaceId,
    refresh,
    signOut,
    canWrite: Boolean(workspace && ['owner', 'admin', 'editor'].includes(workspace.role) && !workspace.is_demo),
    isDemo: Boolean(workspace?.is_demo),
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export const useSession = (): SessionValue => useContext(SessionContext);
